"use client";
import React, { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { generateQRCode } from "./../../api/vietQRApi";
import BankSelector from "./BankSelector";
import DonationAmount from "./DonationAmount";
import { readNumber } from "./../../utils/formatCurrency";

const QRCodePayment = () => {
  const [selectedBank, setSelectedBank] = useState(null);
  const [amount, setAmount] = useState(10);
  const [qrCodeImg, setQrCodeImg] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const mutation = useMutation({
    mutationFn: generateQRCode, // Correct usage
    onSuccess: (data) => {
      
      // Simulate API call delay
      setTimeout(() => {
        setQrCodeImg(data.data.qrDataURL);
        setIsGenerating(false);
      }, 1000);
    },
    onError: (error) => {
      alert(
        "Upload failed: " + (error.response?.data?.message || error.message)
      );
    },
  });

  // Generate QR code URL using VietQR API
  const generateQRCodeUI = () => {
    if (!selectedBank || !amount) return;
    setIsGenerating(true);
    let request = {
      bin: parseInt(selectedBank.bin),
      amount: amount,
    };
    mutation.mutate(request);

    // // In a real implementation, you would call the VietQR API
    // // This is a placeholder URL structure based on VietQR documentation
    // const vietQrUrl = `https://api.vietqr.io/image/${selectedBank.code}/${accountNumber}/${amount}?accountName=${encodeURIComponent(
    //   accountName,
    // )}&addInfo=Donation`;
  };

  // Generate QR code when bank or amount changes
  useEffect(() => {
    if (selectedBank && amount > 0) {
      generateQRCodeUI();
    }
  }, [selectedBank, amount]);

  const handleDownloadQR = () => {
    // In a real implementation, you would download the QR code image
    alert("QR Code download functionality would be implemented here");
  };

  return (
    <div className="flex flex-col md:flex-row gap-8">
      <div className="flex-1">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Chuyển khoản ngân hàng qua mã QR
        </h2>
        <p className="text-gray-600 mb-6">
          Quét mã QR bằng ứng dụng ngân hàng của bạn để quyên góp trực tiếp vào
          tài khoản của chúng tôi. Chọn ngân hàng của bạn và nhập số tiền bạn
          muốn quyên góp.
        </p>

        <div className="space-y-6">
          <BankSelector
            selectedBank={selectedBank}
            onBankChange={setSelectedBank}
          />

          <DonationAmount onAmountChange={setAmount} currency="₫" />

          <button
            onClick={generateQRCodeUI}
            disabled={!selectedBank || amount <= 0 || isGenerating}
            className="w-full py-2 px-4 bg-rose-700 text-white rounded-md hover:bg-rose-800 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? "Đang tạo..." : "Tạo mã QR"}
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center">
        {qrCodeImg ? (
          <div className="text-center">
            <div className="bg-white p-4 rounded-lg border border-gray-200 inline-block mb-4">
              {/* In a real implementation, this would be the actual QR code image */}
              <div className="w-64 h-64 bg-gray-100 flex items-center justify-center">
                {!isGenerating ? (
                  <img src={qrCodeImg} alt="" />
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                    />
                  </svg>
                )}
              </div>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Ngân hàng:{" "}
                <span className="font-medium">{selectedBank?.name}</span>
              </p>
              <p className="text-sm text-gray-600">
                Tài khoản:{" "}
                <span className="font-medium">
                  {selectedBank?.accountNumber}
                </span>
              </p>
              <p className="text-sm text-gray-600">
                Số tiền:{" "}
                <span className="font-medium">
                  {new Intl.NumberFormat("vi-VN", {
                    style: "currency",
                    currency: "VND",
                  }).format(amount)}
                </span>
                <span className="block font-medium">
                  Bằng chữ: {amount && readNumber(amount)}
                </span>
              </p>
            </div>
            <button
              onClick={handleDownloadQR}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500"
            >
              <svg
                className="-ml-1 mr-2 h-5 w-5 text-gray-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Tải mã QR
            </button>
          </div>
        ) : (
          <div className="text-center p-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              Chưa có mã QR được tạo
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Chọn ngân hàng và số tiền để tạo mã QR thanh toán.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRCodePayment;
